// RiskReports.js (formerly RisksByModel.js)
// Enhanced assessment report cards with Builder/Explorer distinction

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { jwtDecode } from 'jwt-decode';
import {
  Container,
  Stack,
  Group,
  Title,
  Text,
  Card,
  Avatar,
  Grid,
  ActionIcon,
  Tooltip,
  Center,
  ThemeIcon,
  Table,
  SegmentedControl,
  Divider,
  Badge,
  Chip,
  Progress,
  RingProgress,
} from '@mantine/core';
import {
  IconEye,
  IconLayoutGrid,
  IconList,
  IconAlertCircle,
  IconTool,
  IconCompass,
  IconGlobe,
  IconShieldCheck,
  IconLink,
  IconClipboardCheck,
  IconUser,
  IconExternalLink,
  IconChartBar,
} from '@tabler/icons-react';
import { isRiskManager } from './utils/roleHelpers';
import ReviewStatusBadge from './components/ReviewStatusBadge';

// Priority colors (consistent with other components)
const PRIORITY_COLORS = {
  Critical: { bg: '#1A1A1A', text: '#fff' },
  High: { bg: '#993333', text: '#fff' },
  Significant: { bg: '#B94D4D', text: '#fff' },
  Moderate: { bg: '#D4AA7D', text: '#333' },
  Low: { bg: '#B8C2B7', text: '#333' },
  Negligible: { bg: '#D8D8D8', text: '#333' },
};

// Explorer organizational risk areas
const EXPLORER_AREAS = {
  commercialRisk: { label: 'Com', fullLabel: 'Commercial', color: 'blue' },
  operationalRisk: { label: 'Ops', fullLabel: 'Operational', color: 'orange' },
  complianceRisk: { label: 'Cmp', fullLabel: 'Compliance', color: 'red' },
  societalRisk: { label: 'Soc', fullLabel: 'Societal', color: 'violet' },
  environmentalRisk: { label: 'Env', fullLabel: 'Environmental', color: 'green' },
  reputationalRisk: { label: 'Rep', fullLabel: 'Reputational', color: 'cyan' },
};

/**
 * RiskReports - Enhanced assessment report cards
 * Shows completed assessments with metrics, distinguishing Builder vs Explorer
 */
function RiskReports() {
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated, user } = useAuth0();
  const [viewMode, setViewMode] = useState('grid');
  const [typeFilter, setTypeFilter] = useState('all');
  const [userIsRiskManager, setUserIsRiskManager] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Redux state
  const assessmentsById = useSelector((state) => state.assessments?.byId || {});
  const modelsById = useSelector((state) => state.models?.byId || {});
  const reviewsData = useSelector((state) => state.reviews?.byModel || {});
  const riskItemsState = useSelector((state) => state.riskItems?.byAssessment || {});
  const securityState = useSelector((state) => state.security?.byAssessment || {});
  const controlsState = useSelector((state) => state.controls?.savedControls || {});

  // Get user roles from Auth0 token
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
            scope: 'openid profile email read:models write:models delete:models'
          }
        });

        const decoded = jwtDecode(token);
        const roles = decoded['https://agenthuginn.ai/claims/roles'] || decoded['https://riskgpt.io/claims/roles'] || [];
        setUserIsRiskManager(isRiskManager(roles));
        setCurrentUserId(user?.sub || decoded.sub);
      } catch (error) {
        console.error('[RiskReports] Error fetching user info:', error);
        setUserIsRiskManager(false);
      }
    };

    if (isAuthenticated) {
      fetchUserInfo();
    }
  }, [getAccessTokenSilently, isAuthenticated, user]);

  // Helper to capitalize priority
  const capitalizeFirstLetter = (string) => {
    if (!string) return 'Moderate';
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  // Calculate comprehensive stats for an assessment
  const calculateAssessmentStats = useCallback((assessmentUuid, modelId, assessmentType) => {
    const stats = {
      // Risk counts by priority
      priorities: { Critical: 0, High: 0, Significant: 0, Moderate: 0, Low: 0, Negligible: 0 },
      totalRisks: 0,
      // Action/control stats
      totalActions: 0,
      completedActions: 0,
      // Security (Builder only)
      securityThreats: 0,
      // Explorer-specific
      areaBreakdown: {},
      inheritedRisks: 0,
      // AI scores
      severityScore: 0,
      severityLevel: 'LOW',
      mitigationScore: 0,
      mitigationLevel: 'WEAK',
    };

    const riskData = riskItemsState[assessmentUuid];
    if (!riskData) return stats;

    if (assessmentType === 'explorer') {
      // Explorer assessment: check BOTH explorerRisks AND modelRisks
      // (data may be stored in modelRisks depending on how it was loaded)

      // First check explorerRisks structure
      if (riskData.explorerRisks) {
        Object.entries(riskData.explorerRisks).forEach(([areaKey, area]) => {
          const areaRisks = area?.areaRisks || {};
          let areaRiskCount = 0;

          Object.values(areaRisks).forEach(categoryRisks => {
            if (Array.isArray(categoryRisks)) {
              categoryRisks.forEach(risk => {
                stats.totalRisks++;
                areaRiskCount++;
                const priority = capitalizeFirstLetter(risk.priority);
                if (stats.priorities[priority] !== undefined) {
                  stats.priorities[priority]++;
                }
                // Count controls
                const controlCount = risk.controls?.length || 0;
                stats.totalActions += controlCount;
                stats.completedActions += risk.controls?.filter(c => c.status === 'completed')?.length || 0;
              });
            }
          });

          stats.areaBreakdown[areaKey] = areaRiskCount;
        });
      }

      // Also check modelRisks for explorer data (fallback - data may be stored here)
      if (riskData.modelRisks) {
        Object.entries(riskData.modelRisks).forEach(([sectionKey, section]) => {
          const areaRisks = section?.areaRisks || section?.sectionRisks || {};
          Object.values(areaRisks).forEach(categoryRisks => {
            if (Array.isArray(categoryRisks)) {
              categoryRisks.forEach(risk => {
                if (risk.status === 'Saved') {
                  stats.totalRisks++;
                  stats.inheritedRisks++; // Count as inherited/technical risks
                  const priority = capitalizeFirstLetter(risk.priority || risk.evaluation?.priority);
                  if (stats.priorities[priority] !== undefined) {
                    stats.priorities[priority]++;
                  }
                  // Count controls
                  const controlCount = risk.controls?.length || 0;
                  stats.totalActions += controlCount;
                  stats.completedActions += risk.controls?.filter(c => c.status === 'completed' || c.status === 'Implemented')?.length || 0;
                }
              });
            }
          });
          // Map section to area for breakdown
          if (sectionKey && !stats.areaBreakdown[sectionKey]) {
            const sectionRiskCount = Object.values(areaRisks).reduce((sum, cats) =>
              sum + (Array.isArray(cats) ? cats.filter(r => r.status === 'Saved').length : 0), 0);
            if (sectionRiskCount > 0) {
              stats.areaBreakdown[sectionKey] = sectionRiskCount;
            }
          }
        });
      }
    } else if (riskData.modelRisks) {
      // Builder assessment: count from modelRisks
      Object.values(riskData.modelRisks).forEach(section => {
        const areaRisks = section?.areaRisks || section?.sectionRisks || {};
        Object.values(areaRisks).forEach(categoryRisks => {
          if (Array.isArray(categoryRisks)) {
            categoryRisks.forEach(risk => {
              if (risk.status === 'Saved') {
                stats.totalRisks++;
                const priority = capitalizeFirstLetter(risk.priority || risk.evaluation?.priority);
                if (stats.priorities[priority] !== undefined) {
                  stats.priorities[priority]++;
                }
                // Count controls
                const controlCount = risk.controls?.length || 0;
                stats.totalActions += controlCount;
                stats.completedActions += risk.controls?.filter(c => c.status === 'completed')?.length || 0;
              }
            });
          }
        });
      });

      // Count security threats
      const securityData = securityState[assessmentUuid] || securityState[modelId];
      if (securityData?.scenarios) {
        stats.securityThreats = Object.keys(securityData.scenarios).length;
      }
    }

    // Also check savedControls state for action counts
    const savedControls = controlsState[assessmentUuid] || controlsState[modelId] || {};
    Object.values(savedControls).forEach(riskControls => {
      if (Array.isArray(riskControls)) {
        stats.totalActions += riskControls.length;
        stats.completedActions += riskControls.filter(c => c.status === 'completed' || c.status === 'Implemented').length;
      }
    });

    // Calculate AI Severity Score (0-100)
    if (stats.totalRisks > 0) {
      const { priorities } = stats;
      stats.severityScore = Math.min(100, Math.round(
        ((priorities.Critical * 100) + (priorities.High * 80) + (priorities.Significant * 60) +
         (priorities.Moderate * 40) + (priorities.Low * 20) + (priorities.Negligible * 10)) / stats.totalRisks
      ));
      stats.severityLevel = stats.severityScore >= 70 ? 'HIGH' : stats.severityScore >= 40 ? 'MEDIUM' : 'LOW';
    }

    // Calculate Mitigation Score
    if (stats.totalActions > 0) {
      stats.mitigationScore = Math.round((stats.completedActions / stats.totalActions) * 100);
    } else if (stats.totalRisks > 0) {
      // No actions yet = 0%
      stats.mitigationScore = 0;
    }
    stats.mitigationLevel = stats.mitigationScore >= 70 ? 'STRONG' : stats.mitigationScore >= 40 ? 'MODERATE' : 'WEAK';

    return stats;
  }, [riskItemsState, securityState, controlsState]);

  // Build completed assessments list
  const completedAssessments = useMemo(() => {
    const assessments = [];

    Object.entries(assessmentsById).forEach(([assessmentUuid, assessment]) => {
      const status = assessment.info?.status || assessment.assessmentStatus;
      if (status !== 'Completed') return;

      const modelId = assessment.model_uuid;
      const modelData = modelsById[modelId] || {};

      // Access control
      let canView = userIsRiskManager;
      if (!userIsRiskManager && currentUserId) {
        const isOwner = assessment.info?.created_by === currentUserId || assessment.created_by === currentUserId;
        const reviewData = reviewsData[modelId] || reviewsData[assessmentUuid];
        const isAssignedReviewer = reviewData?.reviewers?.includes(currentUserId);
        canView = isOwner || isAssignedReviewer;
      }

      if (!canView) return;

      const reviewData = reviewsData[modelId] || reviewsData[assessmentUuid] || {};
      const reviewStatus = reviewData.reviewStatus || assessment.review_status || assessment.info?.review_status;
      const rawType = assessment.assessment_type || assessment.info?.assessment_type || 'builder';
      // Normalize: DB stores 'business_case' for Explorer, 'use_case' for Navigator
      const assessmentType = rawType === 'business_case' ? 'explorer'
                           : rawType === 'use_case' ? 'navigator'
                           : rawType;

      // Calculate stats — pass rawType so explorer risk data lookup still works
      const stats = calculateAssessmentStats(assessmentUuid, modelId, rawType === 'business_case' ? 'explorer' : rawType);

      // Get parent assessment info for Explorer
      const parentAssessmentUuid = assessment.parent_assessment_uuid || assessment.info?.parent_assessment_uuid;
      const parentAssessment = parentAssessmentUuid ? assessmentsById[parentAssessmentUuid] : null;

      assessments.push({
        assessmentUuid,
        assessmentName: assessment.info?.assessment_name || assessment.assessment_name || 'Unnamed Assessment',
        modelId,
        modelName: modelData.model_name || assessment.model_name || 'Unknown Model',
        assessmentType,
        status,
        reviewStatus,
        // Owner info
        ownerName: assessment.info?.created_by_name || assessment.owner?.name || 'Unknown',
        ownerAvatar: assessment.info?.avatar || assessment.owner?.avatar,
        ownerId: assessment.info?.created_by || assessment.owner?.id,
        // Reviewer info
        reviewerName: assessment.reviewed_by_name || assessment.review_started_by_name,
        reviewerId: assessment.reviewed_by || assessment.review_started_by,
        reviewedAt: assessment.reviewed_at,
        reviewStartedAt: assessment.review_started_at,
        // Timestamps
        createdAt: assessment.info?.created_at,
        updatedAt: assessment.info?.updated_at || assessment.info?.last_activity_at,
        // Stats
        stats,
        // Explorer-specific
        parentAssessmentUuid,
        parentAssessmentName: parentAssessment?.info?.assessment_name || parentAssessment?.assessment_name,
      });
    });

    assessments.sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
    return assessments;
    // Note: calculateAssessmentStats already depends on riskItemsState, securityState, controlsState
  }, [assessmentsById, modelsById, reviewsData, userIsRiskManager, currentUserId, calculateAssessmentStats]);

  // Filter by type and compute counts
  const { filteredAssessments, typeCounts } = useMemo(() => {
    const builder = completedAssessments.filter(a => a.assessmentType === 'builder').length;
    const navigator = completedAssessments.filter(a => a.assessmentType === 'navigator').length;
    const explorer = completedAssessments.filter(a => a.assessmentType === 'explorer').length;
    let filtered = completedAssessments;
    if (typeFilter !== 'all') {
      filtered = completedAssessments.filter(a => a.assessmentType === typeFilter);
    }
    return { filteredAssessments: filtered, typeCounts: { all: completedAssessments.length, builder, navigator, explorer } };
  }, [completedAssessments, typeFilter]);

  // Navigation handlers
  const handleOpenReport = (assessment) => {
    if (assessment.assessmentType === 'explorer') {
      navigate(`/explorer-report/${assessment.modelId}/${assessment.assessmentUuid}`);
    } else if (assessment.assessmentType === 'navigator') {
      navigate(`/navigator-report/${assessment.modelId}/${assessment.assessmentUuid}`);
    } else {
      navigate(`/assessment-report/${assessment.modelId}`, { state: { assessmentUuid: assessment.assessmentUuid } });
    }
  };

  const handleOpenParentReport = (e, parentUuid, modelId) => {
    e.stopPropagation();
    navigate(`/assessment-report/${modelId}`, { state: { assessmentUuid: parentUuid } });
  };

  // Time since helper
  const getTimeSince = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMins > 0) return `${diffMins}m ago`;
    return 'Just now';
  };

  // Type Badge component
  const TypeBadge = ({ type, size = 'sm' }) => {
    const config = type === 'explorer'
      ? { color: 'grape', icon: IconCompass, label: 'EXPLORER', variant: 'filled' }
      : type === 'navigator'
      ? { color: 'orange', icon: IconGlobe, label: 'NAVIGATOR', variant: 'filled' }
      : { color: 'blue', icon: IconTool, label: 'BUILDER', variant: 'light' };
    const Icon = config.icon;

    return (
      <Badge
        variant={config.variant}
        color={config.color}
        size={size}
        leftSection={<Icon size={12} />}
        radius="md"
        styles={{ root: { fontWeight: 600, letterSpacing: '0.5px' } }}
      >
        {config.label}
      </Badge>
    );
  };

  // Priority badges component
  const PriorityBadges = ({ priorities, totalRisks }) => {
    const priorityOrder = ['Critical', 'High', 'Significant', 'Moderate', 'Low'];
    const activePriorities = priorityOrder.filter(p => priorities[p] > 0).slice(0, 4);

    if (totalRisks === 0) {
      return <Text size="xs" c="dimmed">No risks</Text>;
    }

    return (
      <Tooltip
        label={
          <Stack gap={4}>
            <Text size="xs" fw={600}>{totalRisks} Total Risks</Text>
            {priorityOrder.map(p => priorities[p] > 0 && (
              <Group key={p} gap={8}>
                <Text size="xs">{p}:</Text>
                <Text size="xs" fw={600}>{priorities[p]}</Text>
              </Group>
            ))}
          </Stack>
        }
        withArrow
      >
        <Group gap={3}>
          {activePriorities.map(priority => (
            <Badge
              key={priority}
              size="xs"
              variant="filled"
              radius="sm"
              style={{
                backgroundColor: PRIORITY_COLORS[priority]?.bg || '#888',
                color: PRIORITY_COLORS[priority]?.text || '#fff',
                minWidth: 18,
                padding: '0 4px',
              }}
            >
              {priorities[priority]}
            </Badge>
          ))}
          <Text size="xs" c="dimmed">({totalRisks})</Text>
        </Group>
      </Tooltip>
    );
  };

  // Explorer area breakdown component
  const AreaBreakdown = ({ areaBreakdown }) => {
    // Get all keys with non-zero values
    const activeAreas = Object.entries(areaBreakdown || {}).filter(([, count]) => count > 0);
    const totalRisks = activeAreas.reduce((sum, [, count]) => sum + count, 0);

    if (activeAreas.length === 0) {
      return <Text size="xs" c="dimmed">No area data</Text>;
    }

    // Get display info for a key (use EXPLORER_AREAS if available, else format key)
    const getAreaInfo = (key) => {
      if (EXPLORER_AREAS[key]) {
        return EXPLORER_AREAS[key];
      }
      // Format unknown keys: "commercialRisk" -> "Commercial", "section_1" -> "Section 1"
      const label = key.replace(/Risk$/, '').replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();
      return { label: label.slice(0, 3), fullLabel: label.charAt(0).toUpperCase() + label.slice(1), color: 'gray' };
    };

    return (
      <Tooltip
        label={
          <Stack gap={4}>
            <Text size="xs" fw={600}>Risks by Area ({totalRisks} total)</Text>
            {activeAreas.map(([key, count]) => {
              const info = getAreaInfo(key);
              return (
                <Group key={key} gap={8}>
                  <Text size="xs">{info.fullLabel}:</Text>
                  <Text size="xs" fw={600}>{count}</Text>
                </Group>
              );
            })}
          </Stack>
        }
        withArrow
      >
        <Group gap={2}>
          {activeAreas.slice(0, 6).map(([key, count]) => {
            const info = getAreaInfo(key);
            return (
              <Tooltip key={key} label={info.fullLabel} withArrow>
                <Badge
                  size="xs"
                  variant="filled"
                  color={info.color}
                  radius="sm"
                  style={{ minWidth: 22, padding: '0 3px' }}
                >
                  {count}
                </Badge>
              </Tooltip>
            );
          })}
          {activeAreas.length > 6 && (
            <Text size="xs" c="dimmed">+{activeAreas.length - 6}</Text>
          )}
        </Group>
      </Tooltip>
    );
  };

  // AI Score display component
  const AIScoreDisplay = ({ score, level, label }) => {
    const color = level === 'HIGH' || level === 'STRONG' ? 'green' :
                  level === 'MEDIUM' || level === 'MODERATE' ? 'orange' : 'red';

    return (
      <Tooltip label={`${label}: ${score}%`} withArrow>
        <Group gap={4}>
          <RingProgress
            size={32}
            thickness={3}
            sections={[{ value: score, color }]}
            label={
              <Text size="8px" ta="center" fw={700} c={color}>
                {score}
              </Text>
            }
          />
          <Badge size="xs" color={color} variant="light">
            {level}
          </Badge>
        </Group>
      </Tooltip>
    );
  };

  // Empty state component
  const EmptyState = () => (
    <Card withBorder radius="md" p="xl">
      <Center py={60}>
        <Stack align="center" gap="md">
          <ThemeIcon size={64} radius="xl" variant="light" color="gray">
            <IconAlertCircle size={32} />
          </ThemeIcon>
          <Text size="lg" fw={500} c="dimmed">
            No Completed Assessments
          </Text>
          <Text size="sm" c="dimmed" ta="center" maw={400}>
            Completed risk assessments will appear here. Start an assessment to see reports.
          </Text>
        </Stack>
      </Center>
    </Card>
  );

  // Grid view - enhanced cards
  const renderGridView = () => {
    if (filteredAssessments.length === 0) return <EmptyState />;

    return (
      <Grid>
        {filteredAssessments.map((assessment) => {
          const { stats } = assessment;
          const isExplorer = assessment.assessmentType === 'explorer';

          return (
            <Grid.Col span={{ base: 12, sm: 6, lg: 4 }} key={assessment.assessmentUuid}>
              <Card
                withBorder
                radius="md"
                p="md"
                style={{ cursor: 'pointer', height: '100%' }}
                onClick={() => handleOpenReport(assessment)}
              >
                <Stack gap="sm">
                  {/* Header with name, model, and tagline */}
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                      <Text fw={600} size="sm" lineClamp={2}>
                        {assessment.assessmentName}
                      </Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {assessment.modelName}
                        {isExplorer && assessment.parentAssessmentName && (
                          <> · linked to {assessment.parentAssessmentName}</>
                        )}
                      </Text>
                    </Stack>
                    {/* Badges and avatars on right */}
                    <Stack gap={4} align="flex-end">
                      <Group gap={4}>
                        <TypeBadge type={assessment.assessmentType} size="xs" />
                        <ReviewStatusBadge reviewStatus={assessment.reviewStatus} size="xs" />
                      </Group>
                      <Group gap={4}>
                        <Tooltip label={`Owner: ${assessment.ownerName}`} withArrow>
                          <Avatar size={24} radius="md" src={assessment.ownerAvatar} color="blue">
                            {assessment.ownerName?.charAt(0) || '?'}
                          </Avatar>
                        </Tooltip>
                        {assessment.reviewerName && (
                          <Tooltip label={`Reviewer: ${assessment.reviewerName}`} withArrow>
                            <Avatar size={24} radius="xl" color={assessment.reviewedAt ? 'green' : 'orange'}>
                              <IconClipboardCheck size={14} />
                            </Avatar>
                          </Tooltip>
                        )}
                      </Group>
                    </Stack>
                  </Group>

                  <Divider />

                  {/* AI Scores */}
                  <Group gap="md" justify="space-between">
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" fw={500}>
                        {isExplorer ? 'Org Severity' : 'AI Severity'}
                      </Text>
                      <AIScoreDisplay
                        score={stats.severityScore}
                        level={stats.severityLevel}
                        label="Risk Severity"
                      />
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" fw={500}>Mitigation</Text>
                      <AIScoreDisplay
                        score={stats.mitigationScore}
                        level={stats.mitigationLevel}
                        label="Mitigation Quality"
                      />
                    </Stack>
                  </Group>

                  <Divider />

                  {/* Risks section */}
                  <Stack gap={4}>
                    <Text size="xs" c="dimmed" fw={500}>Risks by Priority</Text>
                    <PriorityBadges priorities={stats.priorities} totalRisks={stats.totalRisks} />
                  </Stack>

                  {/* Actions progress */}
                  <Stack gap={4}>
                    <Group justify="space-between">
                      <Text size="xs" c="dimmed" fw={500}>Actions</Text>
                      <Text size="xs" c="dimmed">
                        {stats.completedActions}/{stats.totalActions || 0}
                      </Text>
                    </Group>
                    <Progress
                      value={stats.totalActions > 0 ? (stats.completedActions / stats.totalActions) * 100 : 0}
                      size="sm"
                      radius="xl"
                      color={stats.totalActions === 0 ? 'gray' : stats.completedActions === stats.totalActions ? 'green' : 'blue'}
                    />
                  </Stack>

                  {/* Type-specific section */}
                  {isExplorer ? (
                    <Stack gap={4}>
                      <Group justify="space-between">
                        <Text size="xs" c="dimmed" fw={500}>Risk Areas</Text>
                        {assessment.parentAssessmentUuid && (
                          <Tooltip label={`View parent: ${assessment.parentAssessmentName}`} withArrow>
                            <ActionIcon
                              size="xs"
                              variant="subtle"
                              color="blue"
                              onClick={(e) => handleOpenParentReport(e, assessment.parentAssessmentUuid, assessment.modelId)}
                            >
                              <IconExternalLink size={12} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                      <AreaBreakdown areaBreakdown={stats.areaBreakdown} />
                      {stats.inheritedRisks > 0 && (
                        <Group gap={4}>
                          <IconLink size={12} color="var(--mantine-color-blue-6)" />
                          <Text size="xs" c="dimmed">
                            {stats.inheritedRisks} technical risks
                          </Text>
                        </Group>
                      )}
                    </Stack>
                  ) : (
                    stats.securityThreats > 0 && (
                      <Group gap={4}>
                        <IconShieldCheck size={14} color="var(--mantine-color-red-6)" />
                        <Text size="xs" c="dimmed">
                          {stats.securityThreats} security threats
                        </Text>
                      </Group>
                    )
                  )}

                  <Divider />

                  {/* Footer with timestamp */}
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">
                      Updated {getTimeSince(assessment.updatedAt)}
                    </Text>
                    <ActionIcon variant="light" color="blue" size="sm" radius="md">
                      <IconEye size={14} />
                    </ActionIcon>
                  </Group>
                </Stack>
              </Card>
            </Grid.Col>
          );
        })}
      </Grid>
    );
  };

  // Table view - enhanced
  const renderTableView = () => {
    if (filteredAssessments.length === 0) return <EmptyState />;

    return (
      <Card withBorder radius="md" p={0}>
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ minWidth: 180 }}>Assessment</Table.Th>
              <Table.Th style={{ width: 80 }}>Type</Table.Th>
              <Table.Th style={{ width: 100 }}>Severity</Table.Th>
              <Table.Th style={{ minWidth: 140 }}>Risks</Table.Th>
              <Table.Th style={{ width: 100 }}>Actions</Table.Th>
              <Table.Th style={{ width: 100 }}>Review</Table.Th>
              <Table.Th style={{ width: 80 }}>Owner</Table.Th>
              <Table.Th style={{ width: 80 }}>Updated</Table.Th>
              <Table.Th style={{ width: 60, textAlign: 'center' }}>View</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filteredAssessments.map((assessment) => {
              const { stats } = assessment;

              return (
                <Table.Tr key={assessment.assessmentUuid}>
                  <Table.Td>
                    <Stack gap={2}>
                      <Text fw={500} size="sm" lineClamp={1}>
                        {assessment.assessmentName}
                      </Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {assessment.modelName}
                      </Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <TypeBadge type={assessment.assessmentType} size="xs" />
                  </Table.Td>
                  <Table.Td>
                    <AIScoreDisplay
                      score={stats.severityScore}
                      level={stats.severityLevel}
                      label="Severity"
                    />
                  </Table.Td>
                  <Table.Td>
                    <PriorityBadges priorities={stats.priorities} totalRisks={stats.totalRisks} />
                  </Table.Td>
                  <Table.Td>
                    <Tooltip label={`${stats.completedActions}/${stats.totalActions} complete`} withArrow>
                      <Group gap={4}>
                        <Progress
                          value={stats.totalActions > 0 ? (stats.completedActions / stats.totalActions) * 100 : 0}
                          size="sm"
                          radius="xl"
                          style={{ width: 50 }}
                          color={stats.totalActions === 0 ? 'gray' : 'blue'}
                        />
                        <Text size="xs" c="dimmed">
                          {stats.completedActions}/{stats.totalActions || 0}
                        </Text>
                      </Group>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td>
                    <ReviewStatusBadge reviewStatus={assessment.reviewStatus} size="xs" />
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <Tooltip label={assessment.ownerName} withArrow>
                        <Avatar size={24} radius="xl" src={assessment.ownerAvatar} color="blue">
                          <IconUser size={12} />
                        </Avatar>
                      </Tooltip>
                      {assessment.reviewerName && (
                        <Tooltip label={`Reviewer: ${assessment.reviewerName}`} withArrow>
                          <Avatar size={24} radius="xl" color={assessment.reviewedAt ? 'green' : 'orange'}>
                            <IconClipboardCheck size={12} />
                          </Avatar>
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">{getTimeSince(assessment.updatedAt)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Center>
                      <Tooltip label="View Report" withArrow>
                        <ActionIcon
                          variant="light"
                          color="blue"
                          size="md"
                          radius="md"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenReport(assessment);
                          }}
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Center>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Card>
    );
  };

  return (
    <Container size="xl" py="lg">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="center">
          <Group gap="sm">
            <IconChartBar size={24} stroke={1.5} color="var(--mantine-color-dimmed)" />
            <Title order={2} fw={500}>Risk Reports</Title>
          </Group>
          <SegmentedControl
            value={viewMode}
            onChange={setViewMode}
            data={[
              { value: 'grid', label: <IconLayoutGrid size={16} /> },
              { value: 'table', label: <IconList size={16} /> },
            ]}
            size="sm"
          />
        </Group>

        {/* Type Filter */}
        <Group gap="xs">
          <Text size="sm" c="dimmed" fw={500} w={50}>Type:</Text>
          <Chip.Group value={typeFilter} onChange={setTypeFilter}>
            <Group gap="xs">
              <Chip value="all" variant="light" size="sm" radius="md">
                All ({typeCounts.all})
              </Chip>
              <Chip value="builder" variant="light" size="sm" radius="md" color="blue">
                Builder ({typeCounts.builder})
              </Chip>
              <Chip value="navigator" variant="light" size="sm" radius="md" color="orange">
                Navigator ({typeCounts.navigator})
              </Chip>
              <Chip value="explorer" variant="light" size="sm" radius="md" color="grape">
                Explorer ({typeCounts.explorer})
              </Chip>
            </Group>
          </Chip.Group>
        </Group>

        <Divider />

        {/* Content */}
        {viewMode === 'grid' ? renderGridView() : renderTableView()}
      </Stack>
    </Container>
  );
}

export default RiskReports;
